import { memo, useMemo } from "react";
import { FileModelType } from "domains/file-manager/interfaces";
import {
  MODEL_CAPABILITIES,
  MODEL_CAPABILITIES_LABELS,
  MODEL_CAPABILITIES_VALUES,
  ModelCapability,
} from "domains/models/constants/capabilities";
import MenuListMultipleChoice from "domains/ui/components/Menu/MenuListMultipleChoice";
import _ from "lodash";

function getLabel(value: ModelCapability, count?: number) {
  const label = MODEL_CAPABILITIES_LABELS[value];

  if (!count) {
    return label;
  }
  return `${label} (${count})`;
}

export interface FilterModelCapabilitiesProps {
  variant: "all" | "image" | "video" | "3d" | "texture" | "video-upscale";
  value: ModelCapability[];
  setValue: (value: ModelCapability[]) => void;
  models?: FileModelType[];
}

export default memo(function FilterModelCapabilities({
  variant,
  value,
  setValue,
  models,
}: FilterModelCapabilitiesProps) {
  const facets = useMemo(() => {
    if (!models) {
      return undefined;
    }
    return models.reduce(
      (acc, model) => {
        MODEL_CAPABILITIES.forEach((capability) => {
          if (
            _.intersection(
              MODEL_CAPABILITIES_VALUES[capability],
              model.meta.capabilities
            ).length > 0
          ) {
            acc[capability] = (acc[capability] ?? 0) + 1;
          }
        });
        return acc;
      },
      {
        "image-txt": 0,
        "image-img": 0,
        "texture-txt": 0,
        "texture-img": 0,
        "3d-txt": 0,
        "3d-img": 0,
        "video-txt": 0,
        "video-img": 0,
        "video-video": 0,
      } as Record<ModelCapability, number>
    );
  }, [models]);

  const options: { value: ModelCapability; label: string }[] = useMemo(() => {
    if (variant === "video-upscale") {
      return [
        {
          value: "video-video",
          label: getLabel("video-video", facets?.["video-video"]),
        },
      ];
    }
    return _.compact(
      MODEL_CAPABILITIES.map((capability) => {
        const type = capability.split("-")[0];
        if (variant === "all" || variant === type) {
          return {
            value: capability,
            label: getLabel(capability, facets?.[capability]),
          };
        }
        return null;
      })
    );
  }, [facets, variant]);

  return (
    <MenuListMultipleChoice
      label="Capabilities"
      options={options}
      setSelectedValues={setValue}
      selectedValues={value}
      withoutSearch
    />
  );
});
